import React, { useEffect } from 'react';
import { getBrandName } from '../utils/brand';

// `c` wraps inline code consistently across this page.
const c = text => <code>{text}</code>;

// Single tool entry: name + one-line description + an example prompt the user might type.
const Tool = ({ name, description, example }) => (
    <li className="mcp-tool">
        <code className="mcp-tool-name">{name}</code>
        <span className="mcp-tool-desc"> — {description}</span>
        <div className="mcp-tool-example">
            <em>Try:</em> "{example}"
        </div>
    </li>
);

const TOOL_GROUPS = [
    {
        title: 'Organization & Auth',
        tools: [
            {
                name: 'kanbanzone_get_me',
                description: 'Verify the API key works and return the organization name.',
                example: 'Is my Kanban Zone connection working?',
            },
            {
                name: 'kanbanzone_get_organization',
                description: 'Fetch the org profile, optionally including members, boards, columns, labels, and custom fields.',
                example: 'List every board with its columns.',
            },
        ],
    },
    {
        title: 'Boards',
        tools: [
            {
                name: 'kanbanzone_list_boards',
                description: 'List all boards in the organization with optional related data.',
                example: 'What boards do I have?',
            },
            {
                name: 'kanbanzone_get_board',
                description: 'Fetch a single board by its publicId.',
                example: 'Show me the OeMrbG8g board with its columns.',
            },
            {
                name: 'kanbanzone_list_board_columns',
                description: 'List columns on a board, with optional title filter.',
                example: 'List columns on the OeMrbG8g board.',
            },
            {
                name: 'kanbanzone_list_board_labels',
                description: 'List labels available on a board.',
                example: 'What labels are available on this board?',
            },
            {
                name: 'kanbanzone_list_board_members',
                description: 'List members who can be assigned cards on a board.',
                example: 'Who can I assign cards to on this board?',
            },
            {
                name: 'kanbanzone_list_board_custom_fields',
                description: 'List custom fields configured on a board.',
                example: 'What custom fields does this board have?',
            },
        ],
    },
    {
        title: 'Cards',
        tools: [
            {
                name: 'kanbanzone_create_card',
                description: 'Create a single card on a board.',
                example: 'Create a card titled "Refactor auth" on board OeMrbG8g.',
            },
            {
                name: 'kanbanzone_list_cards',
                description: 'List cards on a board with filters and pagination.',
                example: 'What cards are in progress on the OeMrbG8g board?',
            },
            {
                name: 'kanbanzone_get_card',
                description: 'Fetch one card by its ObjectId.',
                example: 'Show me card 6700aabbccddeeff00112233.',
            },
            {
                name: 'kanbanzone_update_card',
                description: 'Update fields on an existing card.',
                example: 'Reassign card 670... to bob@example.com.',
            },
            {
                name: 'kanbanzone_move_card',
                description: 'Move a card to a different column (and optionally a different board).',
                example: 'Move card 670... to the In Progress column.',
            },
            {
                name: 'kanbanzone_get_card_history',
                description: 'Fetch the activity history for a card from a given date.',
                example: 'What happened to card 670... since 2025-01-01?',
            },
            {
                name: 'kanbanzone_get_card_metrics',
                description: 'Fetch time-in-column and cycle/lead-time metrics for a card.',
                example: 'How long has card 670... been in each column?',
            },
        ],
    },
    {
        title: 'Comments',
        tools: [
            {
                name: 'kanbanzone_create_comment',
                description: 'Add a comment to a card.',
                example: 'Add a comment to card 670... saying: blocked on design review.',
            },
            {
                name: 'kanbanzone_list_card_comments',
                description: 'List all comments on a card, oldest first.',
                example: 'What have people said on card 670...?',
            },
        ],
    },
    {
        title: 'Checklists',
        tools: [
            {
                name: 'kanbanzone_create_checklist',
                description: 'Create a checklist on a card, optionally pre-populated with tasks (position is taken from array index).',
                example: 'Add a checklist to card 670... with tasks: write tests, update docs.',
            },
            {
                name: 'kanbanzone_update_checklist',
                description: 'Update title or position of a checklist.',
                example: 'Rename checklist 670... to "QA tasks".',
            },
            {
                name: 'kanbanzone_list_card_checklists',
                description: 'List all checklists on a card, including their tasks.',
                example: "What checklists are on card 670... and what's left to do?",
            },
        ],
    },
    {
        title: 'Tasks',
        tools: [
            {
                name: 'kanbanzone_create_task',
                description: 'Add a task to an existing checklist.',
                example: 'Add a task "Update README" to checklist 670...',
            },
            {
                name: 'kanbanzone_update_task',
                description: 'Update fields on an existing task — including completion.',
                example: 'Mark task 670... as complete.',
            },
            {
                name: 'kanbanzone_move_task',
                description: 'Move a task between checklists or reorder within one.',
                example: 'Move task 670... from checklist A to checklist B at position 0.',
            },
        ],
    },
];

const MCPServer = () => {
    const brandName = getBrandName();
    useEffect(() => {
        document.title = `MCP Server · ${brandName} Docs`;
    }, [brandName]);

    return (
        <div className="swagger-ui doc-page">
            <div className="information-container wrapper">
                <div className="info">
                    <h2 className="title">{brandName} MCP Server</h2>
                    <p>
                        Connect AI assistants like Claude Desktop, Claude Code, Cursor, VS Code, and
                        Windsurf directly to your {brandName} workspace via the{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://modelcontextprotocol.io/"
                        >
                            Model Context Protocol
                        </a>
                        . The server wraps the public API and exposes 23 tools for managing boards,
                        cards, comments, checklists, and tasks.
                    </p>

                    <h2 id="overview">Overview</h2>
                    <p>
                        The {brandName} MCP server is a small Node.js process that runs on your
                        machine and speaks MCP over stdio. Your AI assistant launches it as a
                        subprocess and calls the tools below to read and modify your workspace.
                    </p>
                    <h3>What you can do</h3>
                    <ul className="circle">
                        <li>Orchestrate workflows — create cards, move them between columns, assign owners.</li>
                        <li>Build reports — pull cycle-time, history, and column metrics into a summary.</li>
                        <li>Draft cards from research — turn meeting notes or specs into a checklist.</li>
                        <li>Manage checklists and tasks — including bulk task creation in one call.</li>
                        <li>Query board state — labels, members, custom fields, columns.</li>
                    </ul>
                    <h3>Supported clients</h3>
                    <ul className="circle">
                        <li><a className="link" href="#claude-desktop">Claude Desktop</a></li>
                        <li><a className="link" href="#claude-code">Claude Code</a></li>
                        <li><a className="link" href="#cursor">Cursor</a></li>
                        <li><a className="link" href="#vs-code">VS Code</a></li>
                        <li><a className="link" href="#windsurf">Windsurf</a></li>
                        <li><a className="link" href="#generic">Any other stdio MCP client</a></li>
                    </ul>
                    <h3>Authentication</h3>
                    <p>
                        The MCP server authenticates to the public API with HTTP Basic, using the
                        same composite API key you'd use for direct API calls. Generate one in{' '}
                        <strong>Org Settings → Integrations → API Key</strong>. v1 runs locally
                        with the key supplied via an environment variable; a hosted server with
                        OAuth is on the roadmap.
                    </p>
                    <h3>Rate limits</h3>
                    <p>
                        Calls go through the same public API as direct integrations, so the same
                        rate limits apply. See the <a className="link" href="/api">API Reference</a>{' '}
                        for current limits.
                    </p>
                    <h3>Notes</h3>
                    <ul className="circle">
                        <li>Use your API key — your password is never accepted.</li>
                        <li>v1 only runs locally. Hosted MCP support (for Claude.ai web client) is on the roadmap.</li>
                        <li>The server intentionally does not expose any delete operations.</li>
                    </ul>

                    <h2 id="setup">Setup</h2>
                    <h3>Prerequisites</h3>
                    <ul className="circle">
                        <li>Node.js 18 or newer (for <code>npx</code>).</li>
                        <li>An API key from <strong>Org Settings → Integrations → API Key</strong>.</li>
                    </ul>
                    <p>
                        The server is published to npm as{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://www.npmjs.com/package/kanban-zone-mcp-server"
                        >
                            <code>kanban-zone-mcp-server</code>
                        </a>
                        . Source on{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://github.com/kanbanzone/kanban-zone-mcp-server"
                        >
                            GitHub
                        </a>
                        . You don't need to install anything by hand — the snippets below use{' '}
                        <code>npx</code> to fetch and run it on demand.
                    </p>

                    <h3 id="claude-desktop">Claude Desktop</h3>
                    <p>
                        Edit your <code>claude_desktop_config.json</code> — on macOS at{' '}
                        {c('~/Library/Application Support/Claude/claude_desktop_config.json')}, on
                        Windows at {c('%APPDATA%\\Claude\\claude_desktop_config.json')} — and add:
                    </p>
                    <pre><code>{`{
  "mcpServers": {
    "kanbanzone": {
      "command": "npx",
      "args": ["-y", "kanban-zone-mcp-server"],
      "env": {
        "KANBANZONE_API_KEY": "accessId:apiKey"
      }
    }
  }
}`}</code></pre>
                    <p>Restart Claude Desktop. The {brandName} tools should appear in the tool picker.</p>

                    <h3 id="claude-code">Claude Code</h3>
                    <pre><code>{`claude mcp add kanbanzone \\
  -e KANBANZONE_API_KEY=accessId:apiKey \\
  -- npx -y kanban-zone-mcp-server`}</code></pre>

                    <h3 id="cursor">Cursor</h3>
                    <p>Edit Cursor's <code>mcp.json</code> in your settings directory and add an entry like:</p>
                    <pre><code>{`{
  "mcpServers": {
    "kanbanzone": {
      "command": "npx",
      "args": ["-y", "kanban-zone-mcp-server"],
      "env": { "KANBANZONE_API_KEY": "accessId:apiKey" }
    }
  }
}`}</code></pre>

                    <h3 id="vs-code">VS Code</h3>
                    <pre><code>{`code --add-mcp '{"name":"kanbanzone","command":"npx","args":["-y","kanban-zone-mcp-server"],"env":{"KANBANZONE_API_KEY":"accessId:apiKey"}}'`}</code></pre>

                    <h3 id="windsurf">Windsurf</h3>
                    <p>
                        Open <strong>Settings → MCP servers → Add custom server</strong>, then paste the
                        same JSON shape used for Cursor.
                    </p>

                    <h3 id="generic">Any other stdio MCP client</h3>
                    <p>
                        The server speaks the standard MCP stdio protocol. Run{' '}
                        {c('npx -y kanban-zone-mcp-server')} as a subprocess with{' '}
                        {c('KANBANZONE_API_KEY')} set in the environment. To run from source instead,
                        clone the repo and run {c('node src/index.js')} after {c('npm install')}.
                    </p>

                    <h3>Troubleshooting</h3>
                    <ul className="circle">
                        <li><strong>401 errors on every tool call</strong> — the API key is wrong or expired. Regenerate it.</li>
                        <li><strong>Tools don't appear</strong> — run <code>npx -y kanban-zone-mcp-server</code> once manually to confirm it downloads cleanly, then restart your client.</li>
                        <li><strong>"Card does not exist" on a card you can see</strong> — it's likely a mirror. Pass the <code>board</code> publicId to disambiguate.</li>
                        <li><strong>Truncation warnings on <code>list_cards</code></strong> — paginate with <code>page</code>/<code>count</code>, or narrow with filters.</li>
                    </ul>

                    <h2 id="tools">Tools Reference</h2>
                    <p>
                        23 tools, all prefixed with <code>kanbanzone_</code>. Full input/output
                        schemas for the underlying endpoints live in the{' '}
                        <a className="link" href="/api">API Reference</a>. The list below documents
                        agent-facing usage with example prompts.
                    </p>
                    {TOOL_GROUPS.map(group => (
                        <section key={group.title} className="mcp-tool-group">
                            <h3>{group.title} ({group.tools.length})</h3>
                            <ul className="mcp-tool-list">
                                {group.tools.map(tool => (
                                    <Tool key={tool.name} {...tool} />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MCPServer;
